import React, { useState } from 'react';
import { Download, Loader2, AlertCircle, FileText, Calendar } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import AdminLayout from './AdminLayout';

/**
 * ChatExportPage allows system admins to export chat messages
 * within a given date range as a CSV file.
 */
const ChatExportPage = () => {
  // Default to last 30 days
  const today = new Date();
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  const formatDateForInput = (date: Date) => date.toISOString().split('T')[0];

  const [startDate, setStartDate] = useState(formatDateForInput(thirtyDaysAgo));
  const [endDate, setEndDate] = useState(formatDateForInput(today));
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastExport, setLastExport] = useState<{ date: string; count: number } | null>(null);

  const handleExport = async () => {
    setError(null);
    setIsExporting(true);

    try {
      // Validate dates
      if (!startDate || !endDate) {
        throw new Error('Please select both start and end dates');
      }

      const start = new Date(startDate);
      const end = new Date(endDate);

      if (start > end) {
        throw new Error('Start date must be before end date');
      }

      // First, get the count via JSON to show feedback
      const countResponse = await fetch(
        `/api/admin/export/chat-messages?startDate=${startDate}&endDate=${endDate}&format=json`,
        { credentials: 'include' }
      );

      if (!countResponse.ok) {
        const errorData = await countResponse.json().catch(() => ({}));
        throw new Error(errorData.message || `Export failed: ${countResponse.statusText}`);
      }

      const countData = await countResponse.json();
      
      if (countData.totalMessages === 0) {
        setError('No messages found in the selected date range');
        setIsExporting(false);
        return;
      }

      // Now download the CSV
      const csvResponse = await fetch(
        `/api/admin/export/chat-messages?startDate=${startDate}&endDate=${endDate}&format=csv`,
        { credentials: 'include' }
      );

      if (!csvResponse.ok) {
        throw new Error('Failed to download CSV');
      }

      // Create blob and trigger download
      const blob = await csvResponse.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `chat-export-${startDate}-to-${endDate}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      // Update last export info
      setLastExport({
        date: new Date().toLocaleString(),
        count: countData.totalMessages,
      });

    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setIsExporting(false);
    }
  };

  // Quick date range presets
  const setPreset = (days: number) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    setStartDate(formatDateForInput(start));
    setEndDate(formatDateForInput(end));
  };

  return (
    <AdminLayout
      title="Chat Export"
      description="Export chat messages and AI responses within a date range for reporting and analysis."
    >
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Export Form Card */}
        <Card className="flex h-full flex-col">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-orange-50 dark:bg-orange-900/50 text-orange-600 dark:text-orange-400">
                <Download className="h-4 w-4" />
              </span>
              <div>
                <CardTitle className="text-base">Export Chat Data</CardTitle>
                <CardDescription className="text-xs">
                  Download a CSV of chat messages within the selected timeframe.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="flex flex-1 flex-col space-y-4">
            {/* Date Range Inputs */}
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="startDate" className="flex items-center gap-2 text-xs">
                  <Calendar className="h-3.5 w-3.5 text-slate-400" />
                  Start Date
                </Label>
                <Input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  max={endDate}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="endDate" className="flex items-center gap-2 text-xs">
                  <Calendar className="h-3.5 w-3.5 text-slate-400" />
                  End Date
                </Label>
                <Input
                  id="endDate"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={startDate}
                  max={formatDateForInput(new Date())}
                />
              </div>
            </div>

            {/* Quick Presets */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-slate-500 dark:text-slate-400">Quick:</span>
              <Button variant="outline" size="sm" onClick={() => setPreset(7)}>
                7d
              </Button>
              <Button variant="outline" size="sm" onClick={() => setPreset(30)}>
                30d
              </Button>
              <Button variant="outline" size="sm" onClick={() => setPreset(90)}>
                90d
              </Button>
              <Button variant="outline" size="sm" onClick={() => setPreset(365)}>
                1y
              </Button>
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-center gap-2 text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950 rounded-lg px-3 py-2">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {/* Export Button — pinned to bottom so both cards keep the same height */}
            <Button
              onClick={handleExport}
              disabled={isExporting || !startDate || !endDate}
              className="mt-auto w-full"
            >
              {isExporting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Export to CSV
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="flex h-full flex-col">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
                <FileText className="h-4 w-4" />
              </span>
              <div>
                <CardTitle className="text-base">Export Details</CardTitle>
                <CardDescription className="text-xs">
                  CSV file format reference.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="flex flex-1 flex-col space-y-3">
            <div>
              <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                CSV Columns
              </h4>
              <ul className="space-y-0.5 text-xs text-slate-600 dark:text-slate-400">
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-orange-500" />
                  <strong>Chat ID</strong> – Unique identifier for the chat session
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-orange-500" />
                  <strong>Chat Title</strong> – Title of the chat session
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-orange-500" />
                  <strong>Message ID</strong> – Unique identifier for each message
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-orange-500" />
                  <strong>Role</strong> – "user" or "assistant" (AIVIA)
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-orange-500" />
                  <strong>Content</strong> – The message text
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-orange-500" />
                  <strong>User Email</strong> – Email of the chat owner
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-orange-500" />
                  <strong>Timestamp</strong> – When the message was sent
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-orange-500" />
                  <strong>Feedback Rating</strong> – 1–10 rating submitted on an assistant reply (blank if unrated)
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-orange-500" />
                  <strong>Feedback Comment</strong> – Optional free-text comment submitted with the rating
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-orange-500" />
                  <strong>Feedback Submitted At</strong> – Timestamp the rating was submitted
                </li>
              </ul>
              <p className="mt-2 text-[11px] text-slate-500 dark:text-slate-400">
                Exports exclude legacy-imported chats and internal admin activity.
              </p>
            </div>

            {/* Last Export Info */}
            {lastExport && (
              <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-xs dark:border-green-800 dark:bg-green-950">
                <span className="font-medium text-green-800 dark:text-green-200">
                  Last export:
                </span>{' '}
                <span className="text-green-700 dark:text-green-400">
                  {lastExport.count.toLocaleString()} messages on {lastExport.date}
                </span>
              </div>
            )}

            <p className="mt-auto border-t border-slate-200 pt-2 text-[11px] text-slate-500 dark:border-slate-700 dark:text-slate-400">
              <strong>Note:</strong> Large exports may take a moment — the CSV downloads automatically when ready.
            </p>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default ChatExportPage;
